import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Scale } from 'lucide-react';
import { Button } from './ui/button';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '/#about', label: 'About' },
    { href: '/#practice-areas', label: 'Practice Areas' },
    { href: '/#faq', label: 'FAQ' },
    { href: '/#contact', label: 'Contact' },
  ];

  const legalLinks = [
    { href: '/disclaimer', label: 'Disclaimer' },
    { href: '/privacy-policy', label: 'Privacy Policy' },
  ];

  const scrollToSection = (e, href) => {
    if (href.startsWith('/#')) {
      e.preventDefault();
      const id = href.replace('/#', '');
      if (location.pathname !== '/') {
        window.location.href = href;
        return;
      }
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-md' 
          : 'bg-[#0B1C2D] shadow-sm'
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center space-x-3"
            data-testid="navbar-logo"
          >
            <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${isScrolled ? 'bg-[#0B1C2D]' : 'bg-[#C6A34A]'}`}>
              <Scale className={`w-6 h-6 ${isScrolled ? 'text-[#C6A34A]' : 'text-[#0B1C2D]'}`} />
            </div>
            <div>
              <span 
                className={`text-xl font-semibold block leading-tight ${isScrolled ? 'text-[#0B1C2D]' : 'text-white'}`}
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Juris Bihar
              </span>
              <span className={`text-xs hidden sm:block ${isScrolled ? 'text-[#718096]' : 'text-gray-300'}`}>
                Chambers of Adv. Ajeet Narayan Sinha
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link, index) => (
              <div key={link.href} className="flex items-center">
                <a
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className={`font-medium transition-colors ${isScrolled ? 'text-[#0B1C2D] hover:text-[#C6A34A]' : 'text-white hover:text-[#C6A34A]'}`}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                </a>
                {index < navLinks.length - 1 && (
                  <span className={`ml-8 ${isScrolled ? 'text-gray-300' : 'text-gray-500'}`}>|</span>
                )}
              </div>
            ))}
            <a
              href="/#inquiry-form"
              onClick={(e) => scrollToSection(e, '/#inquiry-form')}
            >
              <Button 
                className={`px-6 ${isScrolled ? 'bg-[#0B1C2D] hover:bg-[#1A2E44] text-white' : 'bg-[#C6A34A] hover:bg-[#D4B055] text-white'}`}
                data-testid="nav-inquiry-btn"
              >
                Submit Legal Query
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`lg:hidden p-2 ${isScrolled ? 'text-[#0B1C2D]' : 'text-white'}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div 
            className="lg:hidden bg-white border-t border-gray-100 py-4"
            data-testid="mobile-menu"
          >
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="block px-4 py-3 text-[#0B1C2D] font-medium hover:bg-gray-50 hover:text-[#C6A34A]"
              >
                {link.label}
              </a>
            ))}
            <div className="border-t border-gray-100 mt-2 pt-2">
              {legalLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="block px-4 py-3 text-[#718096] hover:bg-gray-50 hover:text-[#C6A34A]"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
            </div>
            <div className="px-4 pt-4">
              <a
                href="/#inquiry-form"
                onClick={(e) => scrollToSection(e, '/#inquiry-form')}
              >
                <Button className="w-full bg-[#0B1C2D] hover:bg-[#1A2E44] text-white">
                  Submit Legal Query
                </Button>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
